import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Phone, MessageCircle, RefreshCw, Search } from "lucide-react";
import { api, BUSINESS } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["new", "contacted", "won", "lost"];
const statusColor = {
  new: "bg-blue-100 text-blue-800 border-blue-200",
  contacted: "bg-amber-100 text-amber-800 border-amber-200",
  won: "bg-green-100 text-green-800 border-green-200",
  lost: "bg-red-100 text-red-800 border-red-200",
};

export default function AdminDashboard() {
  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");
    return () => el.setAttribute("content", "index, follow");
  }, []);

  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, won: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const logout = useCallback(() => {
    localStorage.removeItem("sig_token");
    localStorage.removeItem("sig_user");
    nav("/admin/login");
  }, [nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([api.get("/admin/leads"), api.get("/admin/stats")]);
      setLeads(l.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (!localStorage.getItem("sig_token")) { nav("/admin/login"); return; }
    load();
  }, [load, nav]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const filtered = leads.filter((l) => {
    const matchQ = `${l.name} ${l.phone} ${l.location} ${l.requirement}`.toLowerCase().includes(q.toLowerCase());
    const matchF = filter === "all" || l.status === filter;
    return matchQ && matchF;
  });

  return (
    <div className="min-h-screen bg-steel-50 font-body" data-testid="admin-dashboard">
      <header className="bg-steel-900 text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-[68px] flex items-center justify-between">
          <div>
            <span className="font-display font-black tracking-tighter">SUDEEKSHA</span>
            <span className="font-label text-[10px] tracking-[0.3em] uppercase text-steel-400 ml-2">Leads Console</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="refresh-btn" className="flex items-center gap-2 text-sm text-steel-300 hover:text-white transition-colors">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-2 border border-steel-600 px-4 py-2 text-sm hover:bg-steel-800 transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[["Total", stats.total], ["New", stats.new], ["Contacted", stats.contacted], ["Won", stats.won]].map(([label, val]) => (
            <div key={label} className="bg-white border border-steel-200 p-6" data-testid={`stat-${label.toLowerCase()}`}>
              <div className="font-label text-xs uppercase tracking-wider text-steel-500">{label}</div>
              <div className="font-display font-black text-4xl text-steel-900 mt-1">{val}</div>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-steel-400" />
            <input data-testid="lead-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, area..."
              className="w-full bg-white border border-steel-200 pl-10 pr-4 py-3 focus:outline-none focus:ring-1 focus:ring-steel-500 transition-colors" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {["all", ...STATUSES].map((s) => (
              <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
                className={`px-4 py-2 text-sm font-label capitalize border transition-colors ${filter === s ? "bg-steel-800 text-white border-steel-800" : "bg-white text-steel-700 border-steel-200 hover:border-steel-400"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-steel-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead>
              <tr className="border-b border-steel-200 text-left font-label uppercase text-xs tracking-wider text-steel-500">
                <th className="p-4">Name</th>
                <th className="p-4">Phone</th>
                <th className="p-4">Area</th>
                <th className="p-4 hidden md:table-cell">Requirement</th>
                <th className="p-4">Status</th>
                <th className="p-4 hidden lg:table-cell">Date</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} className="border-b border-steel-100 hover:bg-steel-50 transition-colors" data-testid={`lead-row-${l.id}`}>
                  <td className="p-4 font-semibold text-steel-900">{l.name}</td>
                  <td className="p-4 text-steel-700 whitespace-nowrap">{l.phone}</td>
                  <td className="p-4 text-steel-700">{l.location}</td>
                  <td className="p-4 text-steel-600 hidden md:table-cell max-w-xs truncate">{l.requirement}</td>
                  <td className="p-4">
                    <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                      <SelectTrigger data-testid={`status-select-${l.id}`} className={`w-[130px] capitalize border ${statusColor[l.status] || ""}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4 text-steel-500 hidden lg:table-cell whitespace-nowrap">{new Date(l.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <a href={`tel:${l.phone}`} aria-label={`Call ${l.name}`} className="p-2 text-steel-600 hover:text-steel-900 transition-colors" title="Call"><Phone size={16} aria-hidden="true" /></a>
                      <a href={`https://wa.me/91${l.phone.replace(/\D/g, "").slice(-10)}`} target="_blank" rel="noreferrer" aria-label={`WhatsApp ${l.name}`} className="p-2 text-steel-600 hover:text-green-600 transition-colors" title="WhatsApp"><MessageCircle size={16} aria-hidden="true" /></a>
                      <button onClick={() => remove(l.id)} data-testid={`delete-${l.id}`} aria-label={`Delete lead ${l.name}`} className="p-2 text-steel-600 hover:text-red-600 transition-colors" title="Delete"><Trash2 size={16} aria-hidden="true" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && filtered.length === 0 && (
            <div className="p-16 text-center text-steel-500" data-testid="no-leads">No leads found.</div>
          )}
          {loading && <div className="p-16 text-center text-steel-500">Loading...</div>}
        </div>
      </main>
    </div>
  );
}
